import {useCallback, useEffect, useRef, useState} from "react";
import {useAuthInfo} from "@propelauth/react";
import {API_URL, fetchWithToken} from "../api/apiBase";

/**
 * background tasks (server: backend/tasks). one task per kind + project + company + params, holding the
 * current / last run. started by its feature's own route, then polled at /projects/{id}/tasks/{kind}?params
 * until it is no longer running.
 */
export type TaskStatus = "running" | "done" | "failed";

export interface Task {
    id: string;
    kind: string;
    project_id: string;
    status: TaskStatus;
    started_by: string;
    started_at: string;
    finished_at?: string | null;
    result?: any;
    error?: string | null;
}

const POLL_MS = 2000;

/**
 * a task on a project. polls every 2s while it is running.
 * params pick the task (eg {scope_id}) - must match what the start route passes, leave out for none.
 * onFinish fires once when a run this page saw running finishes (done or failed).
 */
export function useTask(projectId: string | undefined, kind: string,
                        onFinish?: (task: Task) => void, params?: Record<string, string>) {
    const {accessToken} = useAuthInfo();
    const [task, setTask] = useState<Task | null>(null);
    const query = params ? `?${new URLSearchParams(params).toString()}` : "";

    const load = useCallback(() => {
        if (!accessToken || !projectId) return;
        fetchWithToken(`/projects/${projectId}/tasks/${kind}${query}`, accessToken)
            .then((data) => setTask(data))
            .catch(() => {});   // no permission / network - leave the last known state
    }, [accessToken, projectId, kind, query]);

    useEffect(() => {
        load();
    }, [load]);

    const status = task?.status;
    useEffect(() => {
        if (status !== "running") return;
        const timer = setInterval(load, POLL_MS);
        return () => clearInterval(timer);
    }, [status, load]);

    // fire onFinish on running -> done / failed. ref so a new callback each render does not re-trigger
    const onFinishRef = useRef(onFinish);
    onFinishRef.current = onFinish;
    const prevStatus = useRef<TaskStatus | undefined>(undefined);
    useEffect(() => {
        if (prevStatus.current === "running" && task && task.status !== "running") {
            onFinishRef.current?.(task);
        }
        prevStatus.current = task?.status;
    }, [task]);

    /** POST to the feature's start route (it returns the task). throws with the server's message */
    const start = async (url: string, body?: any): Promise<Task> => {
        const res = await fetch(`${API_URL}${url}`, {
            method: "POST",
            headers: {Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json"},
            body: body === undefined ? undefined : JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.detail || data?.message || res.statusText);
        setTask(data);
        return data;
    };

    return {task, running: status === "running", start, reload: load};
}
